import { accessTokenStore, userStore } from 'entities/index';
import { observer } from 'mobx-react';
import { Loading } from 'pages/index';
import React, { useEffect, useRef } from 'react';
import { AppLinks } from '../consts/app-links.enum';
import { config } from 'shared/config';
import { delay } from 'shared/utils';
import { parseExpiryTime } from 'shared/utils';
import { LoadingStatusEnum } from 'shared/types';

const PUBLIC_ROUTES = [
  AppLinks.AboutLink,
  AppLinks.SignInLink,
  AppLinks.SignUpLink,
  '/customer/create-booking/',
];

const isPublicRoute = (pathname: string) => {
  return PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
};

const SessionExpired: React.FC = () => {
  useEffect(() => {
    if (window.location.pathname !== AppLinks.AboutLink) {
      window.location.href = AppLinks.AboutLink;
    }
  }, []);
  return <Loading />;
};

interface GlobalGuardProps {
  children: React.ReactElement;
}

const GlobalGuard: React.FC<GlobalGuardProps> = observer(({ children }) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const isRefreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef<number>(0);

  const startTokenRefresh = (forceRestart = false) => {
    const now = Date.now();

    if (!forceRestart && isRefreshingRef.current) {
      return;
    }

    if (now - lastRefreshTimeRef.current < 1000) {
      return;
    }

    if (!accessTokenStore.isAuth) {
      return;
    }

    lastRefreshTimeRef.current = now;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    if (isRefreshingRef.current) {
      isRefreshingRef.current = false;
    }

    (async () => {
      try {
        isRefreshingRef.current = true;
        do {
          if (signal.aborted) {
            isRefreshingRef.current = false;
            return;
          }
          await accessTokenStore.refreshToken();
          const timeMs = parseExpiryTime(config.REFRESH_TOKEN_WAIT_TIME || '');
          if (accessTokenStore.isAuth && !signal.aborted) {
            await delay(timeMs, signal);
          }
        } while (accessTokenStore.isAuth && !signal.aborted);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.log(error);
        }
      } finally {
        isRefreshingRef.current = false;
      }
    })();
  };

  useEffect(() => {
    startTokenRefresh(false);
  }, []);
  useEffect(() => {
    if (accessTokenStore.isAuth) {
      startTokenRefresh(false);
    }
  }, [accessTokenStore.isAuth]);
  useEffect(() => {
    const handleAppBecameActive = () => {
      if (
        (document.visibilityState === 'visible' || document.hasFocus()) &&
        accessTokenStore.isAuth
      ) {
        startTokenRefresh(true);
      }
    };

    document.addEventListener('visibilitychange', handleAppBecameActive);
    window.addEventListener('focus', handleAppBecameActive);

    return () => {
      document.removeEventListener('visibilitychange', handleAppBecameActive);
      window.removeEventListener('focus', handleAppBecameActive);
    };
  }, []);
  useEffect(() => {
    if (
      accessTokenStore.isLoadingTokenStatus === LoadingStatusEnum.SUCCESS &&
      userStore.isLoadingDataStatus !== LoadingStatusEnum.SUCCESS
    ) {
      userStore.init();
    }
  }, [accessTokenStore.isLoadingTokenStatus]);
  if (
    !accessTokenStore.isAuth &&
    accessTokenStore.isLoadingTokenStatus === LoadingStatusEnum.LOADING
  ) {
    return <Loading />;
  }

  if (
    accessTokenStore.isLoadingTokenStatus === LoadingStatusEnum.ERROR ||
    !accessTokenStore.isAuth
  ) {
    if (isPublicRoute(window.location.pathname)) {
      return children;
    }
    return <SessionExpired />;
  }

  if (
    userStore.isLoadingDataStatus === LoadingStatusEnum.SUCCESS ||
    userStore.isLoadingDataStatus === LoadingStatusEnum.ERROR
  ) {
    return children;
  }

  return <Loading />;
});

export { GlobalGuard };
